import * as cx from 'classnames';
import { LocationDescriptorObject } from 'history';
import isNumber from 'lodash-es/isNumber';
import range from 'lodash-es/range';
import truncate from 'lodash-es/truncate';
import upperFirst from 'lodash-es/upperFirst';
import {
    BACK_TO_TOPICS,
    FORUM,
    FORUM_ADD_QUESTION_IN_COUNSELLING,
    FORUM_ADD_REPLY,
    FORUM_MORE_DISCUSSIONS_FROM_topic,
    FORUM_RECENT_POSTS,
    FORUM_SIMILAR_DISCUSSIONS,
    REPORTED_COMMENTS,
    SEND_COMMENT_FORUM,
} from 'mk/autogenerated/translations/PostDetail.5b4838b68cedea4eeaea9c8389f81232'
import { isAuthenticated, isAuthenticatedAs } from 'mk/bazaar/common/userUtils';
import assetUrl from 'mk/common/assetUrl';
import { photoUrl } from 'mk/photo/photoUrl';
import Comment, { CommentForumPlaceholder } from 'mk2/apps/blogs/components/Comment';
import { CommentCreateForm, CommentFormData } from 'mk2/apps/forum/components/CommentCreateEditForm';
import { MorePosts } from 'mk2/apps/forum/components/MorePosts';
import Post from 'mk2/apps/forum/components/Post';
import PostPlaceholder from 'mk2/apps/forum/components/PostPlaceholder';
import SearchPanel from 'mk2/apps/forum/components/SearchPanel';
import { CONTESTS_TOPIC_ID, DAYS_WITH_CATEGORY_ID, TESTINGS_TOPIC_ID } from 'mk2/apps/forum/constants';
import { Props } from 'mk2/apps/forum/containers/PostDetail/PostDetailPage';
import * as forumUrls from 'mk2/apps/forum/urls';
import { getPageNumberInPost, isCounsellingPost, largestAvailablePhotoVersionOfPost } from 'mk2/apps/forum/utils';
import Btn, { BtnType } from 'mk2/components/Btn';
import CoverBreadcrumbs from 'mk2/components/CoverBreadcrumbs';
import Failure from 'mk2/components/errors/Failure';
import Img from 'mk2/components/Img';
import Link from 'mk2/components/Link';
import Pager from 'mk2/components/Pager';
import { FormPageType } from 'mk2/constants';
import AdSlot from 'mk2/containers/AdSlot/AdSlot';
import Complaint from 'mk2/containers/Complaint/Complaint';
import LoadingSwitch from 'mk2/containers/LoadingSwitch/LoadingSwitch';
import { tupdate } from 'mk2/helpers/types';
import { removePage1, url } from 'mk2/helpers/urls';
import { isSuccess, LoadingState } from 'mk2/reducers/loadingState';
import { interpolate } from 'mk2/services/i18n';
import { subscribeJam, unsubscribeJam } from 'mk2/services/jam';
import { trackReach, TrackReachProduct } from 'mk2/trackReach';
import { parse, stringify } from 'query-string';
import * as React from 'react';
import { Helmet } from 'react-helmet';
import styles from './PostDetail.mscss';

class PostDetail extends React.Component<Props> {
    private textArea: HTMLTextAreaElement;

    public shouldComponentUpdate(n: Props) {
        const p = this.props;

        return p.comments !== n.comments
            || p.currentFormPage !== n.currentFormPage
            || p.pageRef !== n.pageRef
            || (p.post && n.post && p.post.deletedAt !== n.post.deletedAt) // When post is deleted
            || p.complaintsOpened.join() !== n.complaintsOpened.join()
            || p.postComplaint !== n.postComplaint
            || p.variant.loadingState !== n.variant.loadingState
            || p.loadingState !== n.loadingState;
    }

    public componentWillReceiveProps(nextProps: Props) {
        const nextPropsHash = nextProps.location && nextProps.location.hash ? nextProps.location.hash : '';
        if (nextPropsHash !== '' && nextProps.post) {
            const hashValue = nextPropsHash.indexOf('-') !== -1 ? nextPropsHash.split('-')[1] : null;

            if (hashValue === 'last') { // TODO: maybe we can use this also for comment-id (.../#post-12345678)
                const reducerKey = `${nextProps.post.topic.slug}/${nextProps.post.slug}`;
                nextProps.onToggleCommentHighlight(reducerKey, hashValue === 'last' ? hashValue : parseFloat(hashValue));

                // remove hash from url(location)
                const newLocation = {
                    ...nextProps.location,
                    hash: '',
                    state: tupdate(
                        nextProps.location.state,
                        {
                            ignoreScroll: true,
                            skipOnBack: true,
                        },
                    ),
                };
                this.props.history.replace(newLocation as LocationDescriptorObject);
            }
        }
    }

    public componentDidMount() {
        const { counselling, contest, post, numberOfPages, pageRef, onTrackEdit, location, campaign, onStoreEvent, variant } = this.props;

        if (this.isEditable && post) { // post este nemusi byt loadnuty
            onTrackEdit(post.id);
        }

        if (post && numberOfPages !== null && pageRef !== null && isNumber(pageRef) && pageRef === numberOfPages) {
            subscribeJam(`forum:post:${post.id}`);
        }

        if (post && campaign && post.topic.category && post.topic.category.id === DAYS_WITH_CATEGORY_ID) {
            trackReach(location, TrackReachProduct.DAYS_WITH, campaign.name, 'forum');
        }

        if (post && post.topic.id === TESTINGS_TOPIC_ID && post.testing) {
            trackReach(location, TrackReachProduct.TESTING, post.testing.trackedHashtag, 'forum');
        }

        if (post && contest && post.topic.id === CONTESTS_TOPIC_ID) {
            trackReach(location, TrackReachProduct.CONTEST, contest.id, 'forum');
        }

        if (post && counselling) {
            onStoreEvent('counselling_visit', {
                counselling_name: post.topic.slug,
                question: post.topic.slug + '/' + post.slug,
            });
        }

        if (isSuccess(variant.loadingState) && variant.variant && post) {
            onStoreEvent('forum_thread_pageview', {
                variant: variant.variant,
                topic: post.topic.slug,
                post: post.slug,
            });
        }
    }

    public componentDidUpdate(prevProps: Props) {
        const {
            match: { params: { topicSlug, postSlug } }, pageRef, numberOfPages, post,
            counselling, onStoreEvent, contest, location, campaign, variant,
        } = this.props;

        if (pageRef !== prevProps.pageRef
            // Case when we click on link to different post in different topic
            || topicSlug !== prevProps.match.params.topicSlug
            || postSlug !== prevProps.match.params.postSlug) {
            this.load();
        }

        if (post && numberOfPages != null && pageRef != null) {
            // We came to last page - so subscribe for updates
            if (isNumber(pageRef) && pageRef === numberOfPages) {
                subscribeJam(`forum:post:${post.id}`);
            } else { // We left last page - no need to listen for changes
                unsubscribeJam(`forum:post:${post.id}`);
            }
        }

        if (post && campaign && post.topic.category && post.topic.category.id === DAYS_WITH_CATEGORY_ID) {
            if (
                (prevProps.location !== location) ||
                (!prevProps.post) ||
                (prevProps.post && prevProps.post.id !== post.id) ||
                (!prevProps.campaign) ||
                (prevProps.campaign && prevProps.campaign.id !== campaign.id)
            ) {
                trackReach(location, TrackReachProduct.DAYS_WITH, campaign.name, 'forum');
            }
        }

        if (post && post.topic.id === TESTINGS_TOPIC_ID && post.testing) {
            if (
                (prevProps.location !== location) ||
                (!prevProps.post) ||
                (prevProps.post && prevProps.post.id !== post.id)
            ) {
                trackReach(location, TrackReachProduct.TESTING, post.testing.trackedHashtag, 'forum');
            }
        }

        if (post && contest && post.topic.id === CONTESTS_TOPIC_ID) {
            if (
                (prevProps.location !== location) ||
                (!prevProps.post || !prevProps.contest) ||
                (prevProps.post && prevProps.post.id !== post.id) ||
                (prevProps.contest && prevProps.contest.id !== contest.id)
            ) {
                trackReach(location, TrackReachProduct.CONTEST, contest.id, 'forum');
            }
        }

        if (
            (counselling && (
                (post && prevProps.post && post.id !== prevProps.post.id) ||
                (post && !prevProps.post)
            )) ||
            (counselling && !prevProps.counselling)
        ) {
            onStoreEvent('counselling_visit', {
                counselling_name: post.topic.slug,
                question: post.topic.slug + '/' + post.slug,
            });
        }

        // A/B test has been loaded
        if (isSuccess(variant.loadingState) && variant.variant && post) {
            // Post or AB has been loaded
            const ABjustLoaded = !isSuccess(prevProps.variant.loadingState);
            const postJustLoaded = !prevProps.post || prevProps.post.id !== post.id;
            if (ABjustLoaded || postJustLoaded) {
                onStoreEvent('forum_thread_pageview', {
                    variant: variant.variant,
                    topic: post.topic.slug,
                    post: post.slug,
                });
            }
        }
    }

    public componentWillUnmount() {
        const { post, numberOfPages, pageRef } = this.props;

        if (post && numberOfPages !== null && pageRef != null && isNumber(pageRef)) {
            unsubscribeJam(`forum:post:${post.id}`);
        }
    }

    public render() {
        const {
            comments, contest, isIOS, loadingState, mobile,
            onAddCommentUnauthenticated, permissions, post, requestUser, staticContext, currentFormPage, initialValues,
        } = this.props;

        const isAdmin = permissions.indexOf('forum.can_manage_forum') !== -1;
        const isModerator = post && isAuthenticatedAs(requestUser, post.topic.moderators);

        let role;
        if (isAdmin) {
            role = 'admin';
        } else if (isModerator) {
            role = 'moderator';
        } else if (post && isAuthenticatedAs(requestUser, post.author)) {
            role = 'author';
        }

        if (staticContext && post && (post.deletedAt || post.topic.deletedAt)) {
            // Force HTTP 404 if post is deleted
            staticContext.statusCode = 410;
        }

        const canAdd = this.canAddPost();

        return (
            <div className={styles.PostDetail}>
                {this.renderHelmet()}

                {post && (
                    <CoverBreadcrumbs
                        photo={post.topic.coverPhoto}
                        className={styles.PostDetail__breadcrumbs}
                    >
                        <Link key={post.id} to={url(forumUrls.forum, {})}>{FORUM}</Link>
                        <Link key={post.topic.name} to={url(forumUrls.forumTopic, {topicSlug: post.topic.slug})}>{post.topic.name}</Link>
                    </CoverBreadcrumbs>
                )}

                {currentFormPage === FormPageType.Main ? (
                    <>
                        {post ? (
                            <>
                                {/* TODO: Change entity name to match post db entity */}
                                <Complaint
                                    id={post.id}
                                    entity="forum-thread"
                                    className={styles.PostDetail__complaint}
                                    scrollOffset={44 + 44 + 30/* header + 30px spacing */}
                                    inForum
                                />
                                <Post
                                    post={post}
                                    contest={contest}
                                    key={post.id}
                                    mobile={mobile}
                                    className={styles.PostDetail__post}
                                    role={role}
                                    onTopicClickedStoreEvent={this.onTopicClickedStoreEvent}
                                    onReply={this.handleOnPostReply}
                                    hasReply={canAdd}
                                />
                            </>
                        ) : (loadingState === LoadingState.LOADING ? (
                            <PostPlaceholder mobile={mobile}/>
                        ) : null)}

                        <LoadingSwitch
                            loadingState={loadingState}
                            onLoad={this.load}
                            onRenderInit={this.renderInit}
                            onRenderSuccess={this.renderSuccess}
                            onRenderFailure={this.renderFailure}
                            hasData={post && comments && comments.length > 0}
                        />
                    </>
                ) : null}

                {canAdd ? (
                    <CommentCreateForm
                        isEdit={false}
                        isDesktop={!mobile}
                        isIOS={isIOS}
                        textareaRef={this.handleTextareaRef}
                        isTestingComment={false}
                        requestUserPermissions={permissions}
                        initialValues={initialValues}
                        onSave={this.handleOnSaveComment}
                    />
                ) : !isAuthenticated(requestUser) ? (
                    <div
                        className={styles.PostDetail__fakeTextArea}
                        onClick={onAddCommentUnauthenticated}
                    >
                        <p className={styles.PostDetail__fakeTextArea__label}>{FORUM_ADD_REPLY}</p>
                        <div className={styles.PostDetail__fakeTextArea__footer}>
                            <div className={styles.PostDetail__fakeTextArea__footer__fakeToolbar}>
                                <i className={cx('fa', 'fa-smile-o', styles.PostDetail__fakeTextArea__footer__fakeToolbar__icon)} />
                                <i className={cx('fa', 'fa-camera', styles.PostDetail__fakeTextArea__footer__fakeToolbar__icon)} />
                                <Img
                                    src={assetUrl + 'img/photoblog/stickers/0.png'}
                                    width={25}
                                    height={25}
                                />
                            </div>
                            <p className={styles.PostDetail__fakeTextArea__footer__fakeBtn}>{SEND_COMMENT_FORUM}</p>
                        </div>
                    </div>
                ) : null }
            </div>
        );
    }

    private renderHelmet() {
        const { baseUrl, pageRef, numberOfPages, post } = this.props;

        if (!post) {
            return null;
        }

        const hasPageNums: boolean = numberOfPages !== null && pageRef !== null && isNumber(pageRef);

        return (
            <Helmet>
                {hasPageNums && (
                    <link
                        rel="canonical"
                        href={baseUrl + url(
                            forumUrls.forumPostDetail,
                            {topicSlug: post.topic.slug, postSlug: post.slug},
                        )}
                    />
                )}
                {hasPageNums && pageRef > 1 && (
                    <link
                        rel="prev"
                        href={baseUrl + url(
                            forumUrls.forumPostDetail,
                            {topicSlug: post.topic.slug, postSlug: post.slug},
                            removePage1({ page: (pageRef as number) - 1 }),
                        )}
                    />
                )}
                {hasPageNums && pageRef < numberOfPages && (
                    <link
                        rel="next"
                        href={baseUrl + url(
                            forumUrls.forumPostDetail,
                            {topicSlug: post.topic.slug, postSlug: post.slug},
                            { page: (pageRef as number) + 1 },
                        )}
                    />
                )}

                {post.photos.map((photo) => [
                    <meta key="url" property="og:image:url" content={photoUrl(photo, largestAvailablePhotoVersionOfPost(photo, 's1600x1600'))} />,
                    <meta key="width" property="og:image:width" content={photo.width.toString()} />,
                    <meta key="height" property="og:image:height" content={photo.height.toString()} />,
                ])}
            </Helmet>
        );
    }

    private canAddPost() {
        const { post, requestUser, contest, contestAlreadyParticipated, permissions } = this.props;

        const isAdmin = permissions.indexOf('forum.can_manage_forum') !== -1;

        return (
            isAuthenticated(requestUser)
            && post && !post.deletedAt && !post.topic.deletedAt && post.topic.category
            && (!post.locked || isAdmin)
            && (!contest || !contestAlreadyParticipated || isAdmin)
            && !isCounsellingPost(post)
        );
    }

    private renderInit = () => {
        const { mobile, pageRef, numberOfPages } = this.props;
        const hasPager = numberOfPages !== null && numberOfPages > 1 && pageRef !== null && isNumber(pageRef);

        return (
            <div>
                {hasPager && (
                    <Pager
                        id="topPager"
                        className={cx(styles.PostDetail__pager, styles['PostDetail__pager--top'])}
                        page={pageRef as number}
                        numberOfPages={numberOfPages}
                        position="top"
                        history={this.props.history}
                        isMobile={mobile}
                    />
                )}

                {// vykreslit dostatok elementov, tak aby browser nezmensil scrollTop position
                 // pocas LoadingState.LOADING pretoze vyska body je mensia nez scrollTop pozicia
                 // ktoru sa snazime zachovat
                    range(10).map((i) => (
                        <CommentForumPlaceholder
                            key={`placeholder-${i}`}
                            className={cx(
                                styles.PostDetail__commentPlaceholder,
                                i === 0 && hasPager && styles['PostDetail__commentPlaceholder--hasAbove-Pager'],
                                i === 0 && !hasPager && styles['PostDetail__commentPlaceholder--hasAbove-Post'],
                            )}
                        />
                ))}

                {hasPager && (
                    <Pager
                        id="bottomPager"
                        className={cx(styles.PostDetail__pager, styles['PostDetail__pager--bottom'])}
                        page={pageRef as number}
                        numberOfPages={numberOfPages}
                        position="bottom"
                        history={this.props.history}
                        isMobile={mobile}
                    />
                )}
            </div>
        );
    };

    private renderFailure = () => {
        const { staticContext, errorMessage } = this.props;

        return (
            <Failure
                staticContext={staticContext}
                errorMessage={errorMessage}
                onRetry={this.load}
                backButton={
                    <Btn
                        type={BtnType.Blue}
                        label={BACK_TO_TOPICS}
                        icon="fa-arrow-left"
                        link={forumUrls.forumTopics}
                    />
                }
            />
        );
    };

    private renderSuccess = () => {
        const {
            post, comments, mobile, complaintsOpened,
            nextPosts, similarPosts, recentPosts,
            requestUser, permissions, pageRef, numberOfPages, commentToBeHighlighted,
            onTrackEdit, onReport, onToggleMenuMore, onDeleteComment, onAddPostUnauthenticated,
            contest, contestAlreadyParticipated, onMoveUpComment,
            onSendHeart, commentsWithComplaint, counselling,
            location: { hash, search },
            match: { params: { topicSlug } },
        } = this.props;

        // is page number is null (unknown) or incorrect, then renderSuccess() should not be reached
        if (numberOfPages === null || pageRef === null) {
            throw new Error('Missing numberOfPages or pageRef');
        }

        const isAdmin = permissions.indexOf('forum.can_manage_forum') !== -1;
        const isModerator = post && isAuthenticatedAs(requestUser, post.topic.moderators);
        const canAdd = this.canAddPost();
        const showContestAnswers = (
            !contest
            || contest.seeAnswers
            || contestAlreadyParticipated
            || new Date() >  new Date(contest.endDate)  // sutaz uz nebezi
        );
        const isCounselling = isCounsellingPost(post);
        const isContest = !!contest;

        const hashValue = hash && hash.indexOf('-') !== -1 ? hash.split('-')[1] : null;
        const commentToBeHighlightedFromHash = hashValue && (hashValue === 'last' ?
                null /* "last" from hash should be forwarded to and handled by the state */ :
                parseFloat(hashValue)
        );
        const commentToBeHighlightedFromState = commentToBeHighlighted && (commentToBeHighlighted === 'last' ?
                (comments && comments.length > 0 && comments[comments.length - 1].id) :
                commentToBeHighlighted
        );
        let highlightedId = commentToBeHighlightedFromState ? commentToBeHighlightedFromState : commentToBeHighlightedFromHash;
        if (isNaN(highlightedId)) {
            highlightedId = null;
        }

        const hasPager = numberOfPages > 1 && isNumber(pageRef);

        return (
            <React.Fragment>
                {comments.length > 0 && (
                    <React.Fragment>
                        {(isAdmin || isModerator) && commentsWithComplaint.length > 0 && (
                            <div className={styles.PostDetail__complaints}>
                                <h3 className={styles.PostDetail__complaints__heading}>{REPORTED_COMMENTS}</h3>
                                <ul className={styles.PostDetail__complaints__list}>
                                    {commentsWithComplaint.map((comment, index) => (
                                        <li key={index} className={styles.PostDetail__complaints__item}>
                                            <Link
                                                className={styles.PostDetail__complaints__link}
                                                to={{
                                                    pathname: url(forumUrls.forumPostDetail, {topicSlug: post.topic.slug, postSlug: post.slug}),
                                                    search: stringify(removePage1(tupdate(parse(search), {page: getPageNumberInPost(comment)}))),
                                                    hash: `comment-${comment.id}`,
                                                    state: {
                                                        // Comment is managing it's own scroll position
                                                        ignoreScroll: true,
                                                    },
                                                }}
                                            >
                                                <strong className={styles.PostDetail__complaints__author}>{comment.authorUser.username}</strong>
                                                &nbsp;
                                                <span className={styles.PostDetail__complaints__comment}>
                                                {truncate(comment.text, { length: 120 })}
                                            </span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {hasPager && (
                            <Pager
                                id="topPager"
                                className={cx(styles.PostDetail__pager, styles['PostDetail__pager--top'])}
                                page={pageRef as number}
                                numberOfPages={numberOfPages}
                                position="top"
                                history={this.props.history}
                                isMobile={mobile}
                            />
                        )}

                        {comments.map((comment, index) => (
                            <React.Fragment key={comment.id}>
                                {!post.deletedAt && !post.isNoAds && !post.topic.isCommercial && index === 3 && (
                                    /*
                                        <AdSlot
                                            zone="Forum Thread - Rectangle 1 (Desktop)"
                                            className={styles.PostDetail__adSlot__desktop}
                                        />
                                    */
                                    <AdSlot
                                        zone="Forum Thread - Halfpage (Mobile)"
                                        className={styles.PostDetail__adSlot__mobile}
                                    />
                                )}
                                {!post.deletedAt && !post.isNoAds && !post.topic.isCommercial && index === 16 && comments.length >= 18 && (
                                    /*
                                        <AdSlot
                                            zone="Forum Thread - Rectangle 3 (Desktop)"
                                            className={styles.PostDetail__adSlot__desktop}
                                        />
                                    */
                                    <AdSlot
                                        zone="Forum Thread - Rectangle 4 (Mobile)"
                                        className={styles.PostDetail__adSlot__mobile}
                                    />
                                )}

                                {/* TODO: Change entity name to match post db entity */}
                                <Complaint
                                    id={comment.id}
                                    entity="forum-message"
                                    className={styles.PostDetail__comment__complaint}
                                    scrollOffset={44 + 44 + 30/* header + 30px spacing */}
                                    inForum
                                />
                                <Comment
                                    className={cx(
                                        styles.PostDetail__comment,
                                        index === 0 && hasPager && !complaintsOpened[0] && styles['PostDetail__comment--hasAbove-Pager'],
                                        index === 0 && !hasPager && !complaintsOpened[0] && styles['PostDetail__comment--hasAbove-Post'],
                                        complaintsOpened[index] && styles['PostDetail__comment--hasAbove-Complaint'],
                                    )}
                                    comment={comment}
                                    highlighted={comment.id === highlightedId}
                                    requestUser={requestUser}
                                    requestUserPermissions={permissions}
                                    isMobile={mobile}
                                    inForum
                                    followLinks={post.topic.isCommercial || !!counselling}
                                    moderators={post.topic.moderators}

                                    hasBodyHiddenText={!showContestAnswers}
                                    hasBodyDeletedStatus={!post.deletedAt}
                                    hasFooterLike={showContestAnswers && !post.topic.deletedAt}
                                    hasFooterReply={!contest && canAdd}
                                    hasMenu={!post.topic.deletedAt}
                                    hasMenuReport={!contest || showContestAnswers}
                                    hasMenuEdit={!contest || isAdmin}
                                    hasMenuDelete={comment.canDelete && (!contest || isAdmin)}
                                    hasMenuMoveUp={post.topic.id === TESTINGS_TOPIC_ID}

                                    onReply={this.handleOnCommentReply}
                                    onReport={onReport}
                                    onToggleMenuMore={onToggleMenuMore}
                                    onDelete={onDeleteComment}
                                    onTrackEdit={onTrackEdit}
                                    onMoveUp={onMoveUpComment}
                                    onSendHeart={onSendHeart}
                                    onDidHighlighted={this.handleOnDidHighlighted}
                                    counselling={counselling}
                                />
                            </React.Fragment>
                        ))}

                        {hasPager && (
                            <Pager
                                id="bottomPager"
                                className={cx(styles.PostDetail__pager, styles['PostDetail__pager--bottom'])}
                                page={pageRef as number}
                                numberOfPages={numberOfPages}
                                position="bottom"
                                history={this.props.history}
                                isMobile={mobile}
                            />
                        )}

                        {isCounselling &&
                             <Btn
                                key="btn"
                                type={BtnType.Blue}
                                className={styles.PostDetail__ctaBtn}
                                label={FORUM_ADD_QUESTION_IN_COUNSELLING}
                                link={isAuthenticated(requestUser) ? url(forumUrls.forumCounsellingNew, { topicSlug: post.topic.slug }) : null}
                                onClick={isAuthenticated(requestUser) ? null : onAddPostUnauthenticated}
                             />
                        }
                    </React.Fragment>
                )}

                {mobile && (
                    <SearchPanel onSubmit={this.onSearch} className={styles.PostDetail__searchPanel} />
                )}

                {mobile && !isContest && !isCounselling &&
                    <MorePosts
                        key="similar"
                        className={cx(styles.PostDetail__morePosts, styles['PostDetail__morePosts--first'])}
                        posts={similarPosts}
                        topicSlug={topicSlug}
                        title={FORUM_SIMILAR_DISCUSSIONS}
                    />
                }
                {mobile && !isContest &&
                    <MorePosts
                        key="next"
                        className={styles.PostDetail__morePosts}
                        posts={isCounselling ? recentPosts : nextPosts}
                        topicSlug={topicSlug}
                        title={isCounselling
                            ? FORUM_RECENT_POSTS
                            : interpolate(FORUM_MORE_DISCUSSIONS_FROM_topic, {topic: upperFirst(post.topic.name)})
                        }
                    />
                }
                {/* isAnonymous(requestUser) && (forumTopicsWithNewsletterModal.indexOf(topicSlug) >= 0) && (
                    <NewsletterModal
                )/> */}
            </React.Fragment>
        );
    };

    private isEditable = () => {
        const postTime = new Date(this.props.post.created);
        const commentTimeWithDelay = new Date((new Date (postTime)).setMinutes(postTime.getMinutes() + 2));
        const currentTime = new Date(Date.now());
        return commentTimeWithDelay > currentTime;
    };

    private load = () => {
        const { onLoad, match: { params: { topicSlug, postSlug } }, comments, location, pageRef, requestUser, variant, onLoadAB } = this.props;
        const numCommentsBeforeLoad = comments.length;

        // Do not load for anonymous users - so that we save some AJAXs (see ab.py - auth_only)
        // Also do not load if already loaded
        if (isAuthenticated(requestUser) && !isSuccess(variant.loadingState)) {
            onLoadAB();
        }

        onLoad(topicSlug, postSlug, pageRef, location, numCommentsBeforeLoad);
    };

    private onTopicClickedStoreEvent = () => {
        const { onStoreEvent, location: { pathname } } = this.props;
        onStoreEvent('forum_category_clicked', { slug: pathname, from: 'detail' });
    };

    private handleTextareaRef = (ref: HTMLTextAreaElement) => {
        this.textArea = ref;
    };

    private handleOnPostReply = () => {
        const { onReply, post } = this.props;
        onReply(post.author.username, this.textArea);
    };

    private handleOnCommentReply = (username: string) => {
        const { onReply } = this.props;
        onReply(username, this.textArea);
    };

    private handleOnSaveComment = (values: CommentFormData) => {
        const { onAddComment, post} = this.props;
        onAddComment(post.id, values);
    };

    private handleOnDidHighlighted = () => {
        const { onToggleCommentHighlight, post } = this.props;
        const reducerKey = `${post.topic.slug}/${post.slug}`;
        onToggleCommentHighlight(reducerKey, null);
    };

    private onSearch = (query: string) => {
        this.props.history.push(url(forumUrls.forumSearch, {}, { q: query }));
    };
}

export default PostDetail;
